// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShieldCheck, AlertCircle, Loader2, Lock, Clock, Link } from 'lucide-react'
import { LinkInvalidatedScreen } from '@/pages/LinkInvalidatedScreen'
import { OnboardingLayout } from '@/components/OnboardingLayout'
import { Logo } from '@/components/Logo'
import { DateInput } from '@/components/DateInput'
import { LanguageSelector } from '@/i18n/LanguageSelector'
import { cn } from '@/lib/utils'
import { postJson, ApiError } from '@/lib/api'
import { signInWithCustomToken } from '@/lib/firebase'
import { setSession, setPatientFirstName, getDeeplinkToken, clearDeeplinkToken } from '@/lib/session'
import { useLanguage } from '@/i18n/use-language'
import type { VerifyTokenResponse } from '@/intake/types'

type VerifyState = 'idle' | 'loading' | 'mismatch' | 'error' | 'locked'

const MAX_VERIFY_ATTEMPTS = 3

export function VerifyScreen() {
  const { t } = useTranslation()

  const TRUST_POINTS = [
    { icon: ShieldCheck, text: t('verify.trustHipaa') },
    { icon: Lock,        text: t('verify.trustTls') },
    { icon: Link,        text: t('verify.trustSingleUse') },
    { icon: Clock,       text: t('verify.trustExpiry') },
  ]
  const navigate = useNavigate()
  const { setLanguage } = useLanguage()
  const [dob, setDob]                 = useState('')
  const [verifyState, setVerifyState] = useState<VerifyState>('idle')
  const [submitted, setSubmitted]     = useState(false)
  const [shakeKey, setShakeKey]       = useState(0)
  const [verifyAttempt, setVerifyAttempt] = useState(0)

  // Identity = the opaque deeplink token (captured at app entry) + DOB as the confirming
  // factor. verify-token takes only { token, dob } — the backend does not check name, so we
  // don't collect it. On success it mints the patient session JWT.
  async function handleVerify() {
    setSubmitted(true)
    if (verifyState === 'locked' || verifyState === 'loading') return
    if (!dob) {
      setShakeKey(k => k + 1)
      return
    }
    const token = getDeeplinkToken()
    if (!token) {
      navigate('/link-expired')
      return
    }
    setVerifyState('loading')
    try {
      const res = await postJson<VerifyTokenResponse>('/v1/auth/verify-token', { token, dob })
      setSession(res.sessionJwt)
      setPatientFirstName(res.patientFirstName)
      setLanguage(res.language)
      // Identity is proven (deeplink + DOB). Trade the tenant custom token for a Firebase
      // session now, before any provider enrollment; a sign-in failure is a hard stop (no
      // bound Firebase user → the Google-link + bind step can't proceed), so surface it.
      if (res.customToken) {
        try {
          await signInWithCustomToken(res.customToken)
        } catch {
          setVerifyState('error')
          return
        }
      }
      // The single-use token is now consumed server-side; drop it so a later expiry routes
      // to the honest "get a new link" terminal instead of re-verifying a dead token.
      clearDeeplinkToken()
      // Next: the mandatory Google link + identity bind, then phone enrollment (which needs
      // the bound session), then consent/intake.
      navigate('/link-google')
    } catch (err) {
      const isApi = err instanceof ApiError
      const code = isApi ? err.code : ''
      // A consumed/invalid/expired single-use link is a dead end, not a retry.
      if (code === 'token_expired') {
        navigate('/link-expired')
        return
      }
      if (code === 'invalid_token' || code === 'token_used') {
        navigate('/link-invalidated')
        return
      }
      // Only a real 403 dob_mismatch is the patient's fault — count it toward the cap. A
      // network drop or 5xx must not read as "wrong DOB" or burn an attempt.
      const isDobMismatch = isApi && err.status === 403 && code === 'dob_mismatch'
      if (!isDobMismatch) {
        // Not the patient's fault (network/5xx) — surface a neutral error, no shake.
        setVerifyState('error')
        return
      }
      // Wrong DOB → retry, capped client-side (server-side lockout is a follow-up; no 423 today).
      const next = verifyAttempt + 1
      if (next >= MAX_VERIFY_ATTEMPTS) {
        setVerifyState('locked')
      } else {
        setVerifyState('mismatch')
        setShakeKey((k) => k + 1)
        setVerifyAttempt(next)
      }
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') handleVerify()
  }

  function clearMismatch() {
    if (verifyState === 'mismatch' || verifyState === 'error') setVerifyState('idle')
  }

  const isMismatch = verifyState === 'mismatch'
  const isError    = verifyState === 'error'
  const isLocked   = verifyState === 'locked'
  const isLoading  = verifyState === 'loading'

  const formProps = {
    dob,
    isLoading, submitted, shakeKey,
    attempt: verifyAttempt + 1,
    maxAttempts: MAX_VERIFY_ATTEMPTS,
    onDobChange: (v: string) => { setDob(v); clearMismatch() },
    onKeyDown: handleKeyDown,
    onSubmit: handleVerify,
  }

  if (isLocked) return <LinkInvalidatedScreen />

  return (
    <OnboardingLayout>
      {/* ── Mobile ── */}
      <div className="flex flex-col flex-1 md:hidden">
        <div className="flex-shrink-0 bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9] px-6 py-8 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#02C7C3]/20 blur-3xl" />
            <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-white/5 blur-2xl" />
          </div>
          <div className="relative z-10 mb-5" style={{ filter: 'brightness(0) invert(1)' }}>
            <Logo variant="mark" className="h-8 w-auto" aria-hidden />
          </div>
          <div className="relative z-10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#02C7C3]/25 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="h-5 w-5 text-[#02C7C3]" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white leading-tight">{t('verify.screenTitle')}</h1>
                <p className="text-white/60 text-xs mt-0.5">{t('verify.screenSubtitle')}</p>
              </div>
            </div>
            <LanguageSelector variant="ghost" className="flex-shrink-0" />
          </div>
          {/* Deeplink entry indicator */}
          <div className="relative z-10 mt-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#02C7C3]/15 w-fit">
            <Link className="h-3 w-3 text-[#02C7C3]" />
            <span className="text-[10px] font-semibold text-[#02C7C3] uppercase tracking-wide">{t('verify.appointmentLink')}</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pt-6 pb-6">
          {isMismatch && <MismatchBanner />}
          {isError && <ErrorBanner />}
          <VerifyForm {...formProps} />
          <div className="mt-8 pt-5 border-t border-border flex items-center justify-center gap-2">
            <Lock className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{t('common.linkExpiresNote')}</span>
          </div>
        </div>
      </div>

      {/* ── Desktop ── */}
      <div className="hidden md:flex flex-1 overflow-hidden">
        {/* Left trust panel */}
        <div className="flex-1 overflow-hidden relative">
          <div className="absolute inset-0 bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9]" />
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full bg-[#02C7C3]/20 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-[280px] h-[280px] rounded-full bg-white/5 blur-2xl" />
          </div>
          <div className="relative z-10 h-full flex flex-col justify-between px-12 py-14 lg:px-16">
            <div style={{ filter: 'brightness(0) invert(1)' }}>
              <Logo variant="wordmark" className="h-9 w-auto" aria-hidden />
            </div>
            <div>
              <p className="text-[#02C7C3] text-xs font-semibold uppercase tracking-widest mb-4">{t('verify.tagline')}</p>
              <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
                {t('verify.headline')}
              </h1>
              <p className="text-white/65 text-base leading-relaxed max-w-sm mb-10">
                {t('verify.subheadline')}
              </p>
              <div className="flex flex-col gap-4">
                {TRUST_POINTS.map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-4 w-4 text-[#02C7C3]" />
                    </div>
                    <span className="text-white/75 text-sm">{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div />
          </div>
        </div>

        {/* Right form panel */}
        <div className="w-[540px] xl:w-[620px] bg-secondary dark:bg-background flex items-center justify-center px-8 py-10 overflow-y-auto relative">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-20 -right-20 w-[280px] h-[280px] rounded-full bg-primary/4 blur-3xl" />
          </div>
          <div className="relative z-10 w-full">
            <div className="bg-card rounded-3xl border border-border overflow-hidden shadow-xl shadow-black/10">
              <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9]" />
              <div className="p-10">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/8 border border-primary/20">
                    <Link className="h-3 w-3 text-primary" />
                    <span className="text-[10px] font-semibold text-primary uppercase tracking-wide">{t('verify.appointmentLink')}</span>
                  </div>
                  <LanguageSelector />
                </div>
                <div className="flex flex-col items-center mb-7">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#006EF9] to-[#02C7C3] flex items-center justify-center mb-4 shadow-lg shadow-primary/25">
                    <ShieldCheck className="h-7 w-7 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground">{t('verify.screenTitle')}</h2>
                  <p className="text-sm text-muted-foreground mt-1 text-center">{t('verify.formSubtitle')}</p>
                </div>

                {isMismatch && <MismatchBanner />}
                {isError && <ErrorBanner />}
                <VerifyForm {...formProps} />

                <div className="mt-6 pt-5 border-t border-border flex items-center justify-center gap-2">
                  <Lock className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{t('common.encryptionNote')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  )
}

function MismatchBanner() {
  const { t } = useTranslation()
  return (
    <div role="alert" className="flex items-start gap-3 px-4 py-3 rounded-xl bg-destructive/8 border border-destructive/20 mb-5 animate-in fade-in slide-in-from-top-2 duration-200">
      <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-destructive leading-snug">{t('verify.mismatchTitle')}</p>
        <p className="text-xs text-destructive/80 mt-0.5 leading-relaxed">{t('verify.mismatchBody')}</p>
      </div>
    </div>
  )
}

// Shown for transient/server failures (network, 5xx) — distinct from a wrong-DOB mismatch,
// and does not consume a verification attempt.
function ErrorBanner() {
  const { t } = useTranslation()
  return (
    <div role="alert" className="flex items-start gap-3 px-4 py-3 rounded-xl bg-destructive/8 border border-destructive/20 mb-5 animate-in fade-in slide-in-from-top-2 duration-200">
      <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-destructive leading-snug">{t('verify.errorTitle')}</p>
        <p className="text-xs text-destructive/80 mt-0.5 leading-relaxed">{t('verify.errorBody')}</p>
      </div>
    </div>
  )
}


interface VerifyFormProps {
  dob: string
  isLoading: boolean
  submitted: boolean
  shakeKey: number
  attempt: number
  maxAttempts: number
  onDobChange: (v: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onSubmit: () => void
}

function VerifyForm({
  dob,
  isLoading, submitted, shakeKey,
  attempt, maxAttempts,
  onDobChange,
  onKeyDown, onSubmit,
}: VerifyFormProps) {
  const { t } = useTranslation()
  const dobEmpty = submitted && !dob

  return (
    <div
      key={shakeKey}
      className={cn('flex flex-col gap-4', shakeKey > 0 && 'animate-shake')}
      onKeyDown={onKeyDown}
    >
      {/* Date of Birth */}
      <div className="flex flex-col gap-1.5">
        <DateInput
          label={<>{t('verify.dob')} <span className="text-destructive">*</span></>}
          value={dob}
          onChange={onDobChange}
          disabled={isLoading}
          error={dobEmpty}
        />
        {dobEmpty && (
          <p className="text-xs text-destructive flex items-center gap-1">
            <AlertCircle className="h-3 w-3 shrink-0" /> {t('common.required')}
          </p>
        )}
      </div>

      {/* Attempt counter */}
      <p className="text-xs text-muted-foreground">
        {t('verify.attemptCount', { current: attempt, max: maxAttempts })}
      </p>

      {/* CTA */}
      <button
        onClick={onSubmit}
        disabled={isLoading}
        className={cn(
          'mt-1 w-full h-11 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 transition-opacity bg-gradient-to-r from-[#006EF9] to-[#02C7C3] shadow-lg shadow-primary/20',
          isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90 active:opacity-80',
        )}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('common.verifying')}
          </>
        ) : (
          t('verify.verifyButton')
        )}
      </button>
    </div>
  )
}
